import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Pagination, TextField, Typography } from '@totalsoft/rocket-ui';
import { Icon, Table, TableBody } from '@mui/material';
import { useCallback } from 'react';
import FlexBox from 'components/common/FlexBox';
import IconButton from 'components/common/buttons/IconButton';
import { useReactiveVar } from '@apollo/client';
import { CityTableContainer } from '../Styles';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_CITIES } from 'features/city/mutations';
import useSubscription from 'hooks/apollo/useSubscription';
import { CITIES_DELETED } from 'features/city/subscriptions';
import { shouldRefetchListVar } from 'features/city/variables';
import { useTranslation } from 'react-i18next';
import { CityType } from 'features/city/constants';
import { ADMIN_CITIES_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const getNewCity = (type, parentCityId) => ({
  id: crypto.randomUUID(),
  code: null,
  name: null,
  type,
  parentCityId,
  inactive: false,
  siruta: null
});

const CitiesTableComponent = ({
  cities,
  type,
  parentCityId,
  size = 10,
  RowComponent,
  inlineFilters = false,
  selectedIdsVar,
  loading,
  loadingLines = 15,
  onCitiesDeleted,
  cancelAddRef
}) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [inactive, setInactive] = useState(null);

  const [pending, setPending] = useState(false);
  const canWrite = usePermission(ADMIN_CITIES_WRITE);

  const [newCity, setNewCity] = useState(null);
  const adding = useMemo(() => Boolean(newCity), [newCity]);

  const selectedIds = useReactiveVar(selectedIdsVar);

  const filteredCities = useMemo(
    () =>
      cities.filter(city => {
        let condition = true;
        if (search) {
          if (!city.name.toLowerCase().includes(search.toLowerCase())) condition = false;
        }
        if (inactive !== null) {
          if (city.inactive !== inactive) condition = false;
        }
        return condition;
      }),
    [cities, search, inactive]
  );

  const pagedCities = useMemo(() => filteredCities.slice((page - 1) * size, (page - 1) * size + size), [filteredCities, size, page]);

  const handleSearchChange = useCallback(value => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleInactiveChange = useCallback(value => {
    setInactive(!value ? null : value === 'true');
    setPage(1);
  }, []);

  const handleAdd = useCallback(() => {
    setNewCity(getNewCity(type, parentCityId));
  }, [type, parentCityId]);

  const handleCancelAdd = useCallback(() => {
    setNewCity(null);
  }, []);

  useEffect(() => {
    cancelAddRef && cancelAddRef(handleCancelAdd);
  }, [handleCancelAdd, cancelAddRef]);

  // #region Delete

  const [deleteCities] = useMutation(DELETE_CITIES, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleDelete = useCallback(() => {
    if (!window.confirm(t('City.ConfirmDeleteMultiple'))) return;

    setPending(true);

    deleteCities({ variables: { ids: selectedIds } });
  }, [deleteCities, selectedIds, t]);

  useSubscription(CITIES_DELETED, {
    onData: ({ data }) => {
      setPending(false);
      const deletedIds = data.data.citiesDeleted.ids;
      shouldRefetchListVar(true);
      onCitiesDeleted && onCitiesDeleted(deletedIds);
    }
  });

  // #endregion

  const disabled = useMemo(() => !canWrite || loading || pending, [canWrite, loading, pending]);

  const inactiveOptions = useMemo(
    () => [
      { value: 'true', label: t('Yes') },
      { value: 'false', label: t('No') }
    ],
    [t]
  );

  return (
    <>
      <FlexBox gap={inlineFilters ? 2 : 1} alignItems={inlineFilters ? 'end' : 'stretch'} column={!inlineFilters} px={1}>
        <FlexBox grow>
          <TextField
            fullWidth
            isClearable
            startAdornment={<Icon sx={{ mr: 0.5 }}>search</Icon>}
            label={t('City.Search')}
            value={search || ''}
            onChange={handleSearchChange}
            disabled={loading}
          />
        </FlexBox>
        <FlexBox grow gap={2} alignItems='end'>
          <FlexBox grow minWidth={0}>
            <Autocomplete
              fullWidth
              label={t('City.Inactive')}
              value={String(inactive) || ''}
              isClearable
              onChange={handleInactiveChange}
              valueKey='value'
              labelKey='label'
              simpleValue
              options={inactiveOptions}
              disabled={loading}
            />
          </FlexBox>
          <FlexBox gap={0.5}>
            <IconButton
              icon='remove_done'
              size='tiny'
              tooltip={t('City.ClearSelection')}
              disabled={selectedIds.length === 0 || disabled}
              onClick={() => selectedIdsVar([])}
            />
            <IconButton
              type='delete'
              size='tiny'
              color='error'
              tooltip={t('City.DeleteSelected')}
              disabled={selectedIds.length === 0 || disabled}
              onClick={handleDelete}
            />
            {!adding && <IconButton type='add' size='tiny' onClick={handleAdd} disabled={disabled} />}
            {adding && <IconButton type='cancel' size='tiny' tooltip={t('City.CancelAdd')} onClick={handleCancelAdd} disabled={disabled} />}
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <CityTableContainer>
        {loading && <WrappedFakeText lines={loadingLines} />}
        {!loading && pagedCities.length === 0 && !newCity && (
          <Typography color='textPrimary' align='center' fontStyle='italic' sx={{ mt: 2 }}>
            {t(`City.No${type === CityType.District ? 'Districts' : 'Cities'}`)}
          </Typography>
        )}
        {!loading && (pagedCities.length > 0 || Boolean(newCity)) && (
          <Table>
            <TableBody>
              {newCity && <RowComponent data={newCity} />}
              {pagedCities.map(city => (
                <RowComponent data={city} key={city.id} />
              ))}
            </TableBody>
          </Table>
        )}
      </CityTableContainer>
      <Pagination
        page={page - 1}
        pageSize={size}
        count={filteredCities.length}
        onPageChange={page => setPage(page + 1)}
        rowsPerPageOptions={-1}
      />
    </>
  );
};

CitiesTableComponent.propTypes = {
  cities: PropTypes.array,
  type: PropTypes.number,
  parentCityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.number,
  RowComponent: PropTypes.node,
  inlineFilters: PropTypes.bool,
  selectedIdsVar: PropTypes.object,
  loading: PropTypes.bool,
  loadingLines: PropTypes.number,
  onCitiesDeleted: PropTypes.func,
  cancelAddFlagId: PropTypes.string,
  cancelAddRef: PropTypes.func
};

export default CitiesTableComponent;
